import { useState, useEffect } from 'react';
import { Cloud, CheckCircle, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AutoSaveIndicatorProps {
  hasChanges: boolean;
  lastSaved?: Date | null;
  savingState?: 'idle' | 'saving' | 'saved' | 'error';
  className?: string;
  variant?: 'compact' | 'full';
}

export function AutoSaveIndicator({ 
  hasChanges, 
  lastSaved = null, 
  savingState = 'idle',
  className = '',
  variant = 'full'
}: AutoSaveIndicatorProps) {
  const [showSaved, setShowSaved] = useState(false);
  
  // Auto hide the "Saved" status after a few seconds
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    
    if (savingState === 'saved') {
      setShowSaved(true);
      timeout = setTimeout(() => {
        setShowSaved(false);
      }, 3000);
    }
    
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [savingState]);
  
  const formatTimeAgo = (date: Date) => {
    if (!date) return '';
    
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    
    if (seconds < 60) {
      return 'just now';
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    } else {
      const hours = Math.floor(seconds / 3600);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    }
  };
  
  // Compact version (icon only with tooltip)
  if (variant === 'compact') {
    let icon = <Cloud className="h-4 w-4 text-gray-400" />;
    let color = 'text-gray-400';
    
    if (savingState === 'saving') {
      icon = <Cloud className="h-4 w-4 animate-pulse text-blue-500" />;
      color = 'text-blue-500';
    } else if (savingState === 'saved' && showSaved) {
      icon = <CheckCircle className="h-4 w-4 text-green-500" />;
      color = 'text-green-500';
    } else if (savingState === 'error') {
      icon = <AlertCircle className="h-4 w-4 text-red-500" />;
      color = 'text-red-500';
    }
    
    return (
      <div className={cn("relative", className)} title={
        savingState === 'saving' ? 'Saving...' :
        savingState === 'saved' ? `Saved ${lastSaved ? formatTimeAgo(lastSaved) : ''}` :
        savingState === 'error' ? 'Error saving' :
        hasChanges ? 'Unsaved changes' : 'All changes saved'
      }>
        <div className={cn("rounded-full p-1", color)}>
          {icon}
        </div>
      </div>
    );
  }
  
  // Full version with text
  return (
    <div className={cn("flex items-center gap-1.5 text-xs font-medium rounded-full py-1 px-2 border", className, {
      'bg-gray-50 text-gray-500 border-gray-200': !hasChanges && savingState !== 'saving' && savingState !== 'error' && (!showSaved || savingState !== 'saved'),
      'bg-blue-50 text-blue-700 border-blue-100': savingState === 'saving',
      'bg-green-50 text-green-700 border-green-100': showSaved && savingState === 'saved',
      'bg-red-50 text-red-700 border-red-100': savingState === 'error',
      'bg-yellow-50 text-yellow-700 border-yellow-100': hasChanges && savingState !== 'saving' && savingState !== 'error'
    })}>
      {savingState === 'saving' ? (
        <>
          <Cloud className="h-3.5 w-3.5 animate-pulse" />
          <span>Saving...</span>
        </>
      ) : savingState === 'saved' && showSaved ? (
        <>
          <CheckCircle className="h-3.5 w-3.5" />
          <span>Saved {lastSaved ? formatTimeAgo(lastSaved) : ''}</span>
        </>
      ) : savingState === 'error' ? (
        <>
          <AlertCircle className="h-3.5 w-3.5" />
          <span>Error saving</span>
        </>
      ) : hasChanges ? (
        <>
          <Cloud className="h-3.5 w-3.5" />
          <span>Unsaved changes</span>
        </>
      ) : (
        <>
          <CheckCircle className="h-3.5 w-3.5" />
          <span>All changes saved</span>
        </>
      )}
    </div>
  );
} 